/**
 * src/pages/ApiKeys.js
 * API key management page — list, generate, revoke keys.
 * Admin only.
 */

import { useState, useEffect } from 'react';
import api from '../api/client';

export default function ApiKeys() {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Generate form state
  const [showForm, setShowForm] = useState(false);
  const [formType, setFormType] = useState('write');
  const [formLabel, setFormLabel] = useState('');
  const [creating, setCreating] = useState(false);
  const [formError, setFormError] = useState('');

  // Newly created key (shown once)
  const [newKey, setNewKey] = useState(null);
  const [copied, setCopied] = useState(false);

  // Revoke state
  const [revokingId, setRevokingId] = useState(null);
  const [confirmRevokeId, setConfirmRevokeId] = useState(null);

  async function fetchKeys() {
    setError('');
    setLoading(true);
    try {
      const res = await api.get('/portal/api-keys');
      setKeys(res.data);
    } catch (err) {
      if (!err.response) {
        setError('Unable to reach the server. Check your connection and try again.');
      } else {
        setError(err.response.data?.message || 'Failed to load API keys.');
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { fetchKeys(); }, []);

  async function handleCreate(e) {
    e.preventDefault();
    setFormError('');
    setError('');
    setSuccess('');
    setCreating(true);
    try {
      const res = await api.post('/portal/api-keys', {
        type: formType,
        label: formLabel.trim() || undefined
      });
      setNewKey(res.data);
      setShowForm(false);
      setFormType('write');
      setFormLabel('');
      await fetchKeys();
    } catch (err) {
      if (!err.response) {
        setFormError('Unable to reach the server. Check your connection and try again.');
      } else {
        setFormError(err.response.data?.message || 'Failed to create API key.');
      }
    } finally {
      setCreating(false);
    }
  }

  async function handleRevoke(id) {
    setError('');
    setSuccess('');
    setRevokingId(id);
    try {
      await api.patch(`/portal/api-keys/${id}/revoke`);
      setConfirmRevokeId(null);
      await fetchKeys();
      setSuccess('API key revoked.');
    } catch (err) {
      if (!err.response) {
        setError('Unable to reach the server. Check your connection and try again.');
      } else {
        setError(err.response.data?.message || 'Failed to revoke key.');
      }
    } finally {
      setRevokingId(null);
    }
  }

  async function copyToClipboard(text) {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback: user can select text manually
    }
  }

  function dismissNewKey() {
    setNewKey(null);
    setCopied(false);
  }

  if (loading) return (
    <div className="page">
      <div style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Loading…</div>
    </div>
  );

  // Load failed with no data — show only the error, not the misleading empty state
  if (error && keys.length === 0) return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">API Keys</h1>
          <p className="page-subtitle">Manage API keys used by the extension and read API.</p>
        </div>
      </div>
      <div className="alert alert-error">
        {error}
        <button className="alert-retry" onClick={fetchKeys}>Try again</button>
      </div>
    </div>
  );

  const activeKeys = keys.filter(k => !k.revoked_at);
  const revokedKeys = keys.filter(k => k.revoked_at);

  return (
    <div className="page">
      <div className="page-header flex items-center justify-between">
        <div>
          <h1 className="page-title">API Keys</h1>
          <p className="page-subtitle">Manage API keys used by the extension and read API.</p>
        </div>
        <button className="btn btn-primary" onClick={() => { setShowForm(prev => !prev); setNewKey(null); }}>
          {showForm ? 'Cancel' : '+ Generate Key'}
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {/* Newly created key — show once */}
      {newKey && (
        <div className="card" style={{ marginBottom: '24px' }}>
          <div className="card-body">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
              <span style={{ fontSize: '14px', fontWeight: '600' }}>New API key created</span>
              <button className="btn btn-ghost btn-sm" onClick={dismissNewKey}
                style={{ fontSize: '18px', lineHeight: 1, padding: '2px 6px' }}>×</button>
            </div>
            <div style={{
              background: '#fef3c7',
              border: '1px solid #f59e0b',
              borderRadius: '6px',
              padding: '10px 14px',
              marginBottom: '16px',
              fontSize: '13px',
              color: '#92400e',
              lineHeight: '1.5'
            }}>
              Copy this key now — it will not be shown again.
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                className="form-input"
                type="text"
                value={newKey.plaintext_key}
                readOnly
                style={{
                  flex: 1,
                  fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, monospace',
                  fontSize: '12px',
                  background: 'var(--sidebar-bg)',
                  color: '#e2e8f0',
                  userSelect: 'all'
                }}
                onClick={e => e.target.select()}
              />
              <button
                className="btn btn-secondary"
                onClick={() => copyToClipboard(newKey.plaintext_key)}
                style={{ whiteSpace: 'nowrap', minWidth: '70px' }}
              >
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Generate form */}
      {showForm && (
        <div className="card" style={{ marginBottom: '24px' }}>
          <div className="card-header">
            <span className="card-title">Generate New Key</span>
          </div>
          <div className="card-body">
            {formError && <div className="alert alert-error">{formError}</div>}
            <form onSubmit={handleCreate}>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Key Type</label>
                  <select className="form-select" value={formType}
                    onChange={e => setFormType(e.target.value)}>
                    <option value="write">Write — Chrome extension (events + config)</option>
                    <option value="read">Read — Read API (query events)</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Label <span style={{ fontWeight: '400', textTransform: 'none' }}>(optional)</span></label>
                  <input className="form-input" type="text" value={formLabel}
                    onChange={e => setFormLabel(e.target.value)}
                    placeholder="e.g. Production extension, Reporting integration"
                    maxLength={100} />
                </div>
              </div>
              <button type="submit" className="btn btn-primary" disabled={creating}>
                {creating ? 'Generating…' : 'Generate Key'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Active keys table */}
      <div className="card" style={{ marginBottom: '24px' }}>
        <div className="card-header">
          <span className="card-title">Active Keys</span>
          <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
            {activeKeys.length} key{activeKeys.length !== 1 ? 's' : ''}
          </span>
        </div>
        {activeKeys.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">🔑</div>
            <div className="empty-state-title">No active keys</div>
            <div className="empty-state-text">Generate a key to connect the Chrome extension or Read API.</div>
          </div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Key</th>
                  <th>Type</th>
                  <th>Label</th>
                  <th>Created</th>
                  <th style={{ width: '100px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {activeKeys.map(k => (
                  <tr key={k.id}>
                    <td className="td-mono" style={{ fontSize: '12px' }}>
                      {k.key_prefix ? `${k.key_prefix}…` : `sk_${k.key_type}_…`}
                    </td>
                    <td>
                      <span className={`badge ${k.key_type === 'write' ? 'badge-success' : 'badge-neutral'}`}>
                        {k.key_type}
                      </span>
                    </td>
                    <td style={{ color: k.label ? 'var(--text-primary)' : 'var(--text-tertiary)', fontSize: '13px' }}>
                      {k.label || '—'}
                    </td>
                    <td className="td-mono">{new Date(k.created_at).toLocaleString()}</td>
                    <td>
                      {confirmRevokeId === k.id ? (
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <button
                            className="btn btn-danger btn-sm"
                            onClick={() => handleRevoke(k.id)}
                            disabled={revokingId === k.id}
                          >
                            {revokingId === k.id ? 'Revoking…' : 'Confirm'}
                          </button>
                          <button
                            className="btn btn-ghost btn-sm"
                            onClick={() => setConfirmRevokeId(null)}
                            disabled={revokingId === k.id}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          className="btn btn-ghost btn-sm"
                          style={{ color: 'var(--danger)' }}
                          onClick={() => setConfirmRevokeId(k.id)}
                        >
                          Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Revoked keys table */}
      {revokedKeys.length > 0 && (
        <div className="card">
          <div className="card-header">
            <span className="card-title" style={{ color: 'var(--text-secondary)' }}>Revoked Keys</span>
            <span style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>
              {revokedKeys.length} key{revokedKeys.length !== 1 ? 's' : ''}
            </span>
          </div>
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Key</th>
                  <th>Type</th>
                  <th>Label</th>
                  <th>Created</th>
                  <th>Revoked</th>
                </tr>
              </thead>
              <tbody>
                {revokedKeys.map(k => (
                  <tr key={k.id} style={{ opacity: 0.6 }}>
                    <td className="td-mono" style={{ fontSize: '12px' }}>
                      {k.key_prefix ? `${k.key_prefix}…` : `sk_${k.key_type}_…`}
                    </td>
                    <td>
                      <span className="badge badge-danger">
                        <span className="status-dot" />{k.key_type}
                      </span>
                    </td>
                    <td style={{ color: 'var(--text-tertiary)', fontSize: '13px' }}>
                      {k.label || '—'}
                    </td>
                    <td className="td-mono">{new Date(k.created_at).toLocaleString()}</td>
                    <td className="td-mono">{new Date(k.revoked_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
